import path from 'node:path';
import pc from 'picocolors';
import { ApiClient } from '../client.js';
import { DEFAULT_PRODUCT_NAME } from '../constants.js';
import { addSecretName, readAppConfig, writeAppConfig } from '../lib/appConfig.js';
import { mergeEnvFile } from '../lib/envfile.js';
import { isValidSecretName, secretEnvVar, secretNameError } from '../lib/names.js';
import { resolveProfile } from '../profiles.js';
import { runProvision } from './provision.js';
import { emitJson, fail, human, note, ok, type Io } from '../output.js';

interface SecretRegistered {
  /** Secret Manager id, "{slug}-{name}". */
  secret: string;
  /** Runtime env var, e.g. OPENAI_API_KEY. */
  envVar: string;
  /**
   * Whether the platform applied the binding to the live app immediately.
   * False/undefined (older platforms, or the app is still setting up) →
   * a config sync (provision) attaches it instead.
   */
  applied?: boolean;
}

/**
 * Resolve the secret value: --value wins; else prompt with hidden input on a
 * TTY; else (non-interactive / --json without --value) it's an error.
 */
async function resolveValue(
  io: Io,
  name: string,
  flagValue: string | undefined,
): Promise<string> {
  if (typeof flagValue === 'string' && flagValue.length > 0) return flagValue;
  const interactive =
    io.json !== true && Boolean((process.stdin as { isTTY?: boolean }).isTTY) &&
    Boolean((process.stdout as { isTTY?: boolean }).isTTY);
  if (!interactive) {
    if (io.json) {
      emitJson({ ok: false, reason: 'value-required', name });
      process.exit(1);
    }
    fail(
      io,
      `No value for "${name}". Pass --value <v>, or run this in an interactive terminal to be prompted.`,
      1,
      'VALUE_REQUIRED',
    );
  }
  // Loaded lazily so non-interactive runs never pay for the prompt library.
  const { password } = await import('@inquirer/prompts');
  const value = await password({ message: `Value for ${name} (hidden):`, mask: true });
  if (value.length === 0) fail(io, 'A secret value is required.', 1, 'VALUE_REQUIRED');
  return value;
}

/**
 * `ghosty secret <name> [--value <v>]` — register a secret for this app:
 *   1. store the value on the platform (Secret Manager, as {slug}-{name}),
 *   2. record the kebab name in app.config.json (deduped),
 *   3. drop an empty stub line in backend/.env.local for local dev,
 *   4. (human mode) re-run provision so the secret binds to the service.
 * The value is never written to disk or echoed.
 */
export async function runSecret(
  name: string,
  options: { value?: string; dir?: string; json?: boolean; adminUrl?: string },
): Promise<void> {
  const io: Io = { json: options.json === true, productName: DEFAULT_PRODUCT_NAME };
  const dir = path.resolve(process.cwd(), options.dir ?? '.');

  if (!name || !isValidSecretName(name)) {
    fail(io, secretNameError(String(name)), 1, 'INVALID_SECRET_NAME');
  }

  const resolved = resolveProfile(options.adminUrl, undefined, dir);
  if (!resolved) fail(io, 'Not signed in. Run `ghosty login` first.', 1, 'NOT_LOGGED_IN');
  io.productName = resolved.profile.branding?.productName ?? DEFAULT_PRODUCT_NAME;

  let config;
  try {
    config = readAppConfig(dir);
  } catch (err) {
    fail(io, (err as Error).message, 1, 'BAD_APP_TREE');
  }

  const value = await resolveValue(io, name, options.value);
  const envVar = secretEnvVar(name);

  // 1. Store the value on the platform.
  note(io, `Storing ${name} for ${config.slug}...`);
  let registered: SecretRegistered;
  try {
    const client = new ApiClient(resolved.adminUrl, resolved.profile.token);
    registered = await client.post<SecretRegistered>(
      `/builder-api/apps/${encodeURIComponent(config.slug)}/secrets`,
      { name, value },
    );
  } catch (err) {
    fail(io, `Could not store the secret: ${(err as Error).message}`, 1, 'SECRET_FAILED');
  }
  const secretId = registered.secret ?? `${config.slug}-${name}`;
  const boundEnv = registered.envVar ?? envVar;

  // 2. Record the name in app.config.json (deduped, value never written).
  writeAppConfig(dir, addSecretName(config, name));

  // 3. Stub line in backend/.env.local (gitignored) so dev code can read it.
  const envLocalPath = path.join(dir, 'backend', '.env.local');
  mergeEnvFile(envLocalPath, { [boundEnv]: '' });

  // 4. The platform applies the binding to the deployed app immediately
  //    (`applied` in the response). When it couldn't — the app is still
  //    setting up, or an older platform — a config sync (provision) attaches
  //    it. In --json mode we leave that to the caller (so the two JSON
  //    streams don't interleave); the add-secret skill runs `ghosty
  //    provision` next. In human mode, do it automatically.
  if (io.json) {
    emitJson({
      ok: true,
      secret: secretId,
      envVar: boundEnv,
      slug: config.slug,
      applied: registered.applied === true,
    });
    return;
  }

  if (registered.applied === true) {
    ok(io, `Stored secret ${pc.bold(name)} — live on the deployed app as ${pc.bold(boundEnv)}.`);
    note(io, `  Recorded in app.config.json and stubbed ${boundEnv}= in backend/.env.local.`);
    human(io, `  Put a real local value in backend/.env.local for development.`);
    return;
  }

  ok(io, `Stored secret ${pc.bold(name)} (binds as ${pc.bold(boundEnv)}).`);
  note(io, `  Recorded in app.config.json and stubbed ${boundEnv}= in backend/.env.local.`);
  human(io, `  Put a real local value in backend/.env.local for development.`);
  note(io, '  Syncing the app configuration…');
  await runProvision({ dir: options.dir, adminUrl: options.adminUrl });
}
